// src/components/proces_verbal/List.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Alert,
  Spinner,
  Form,
  InputGroup,
} from "react-bootstrap";
import ReactPaginate from "react-paginate";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { authFetch } from "../utils/authFetch";

const toYMD = (d) => format(d, "yyyy-MM-dd");

function formatDateRo(val) {
  if (!val) return "";
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

function SortHeader({ label, col, sortBy, sortDir, onSort }) {
  const active = sortBy === col;
  const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "↕";
  return (
    <th
      role="button"
      className="user-select-none"
      onClick={() => onSort(col)}
      title={`Sort by ${label}`}
    >
      <span className="me-1">{label}</span>
      <span style={{ opacity: 0.6, fontSize: 12 }}>{arrow}</span>
    </th>
  );
}

export default function ProcesVerbalList() {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // filtre
  const [searchClient, setSearchClient] = useState("");
  const [searchDocNo, setSearchDocNo] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // sort
  const [sortBy, setSortBy] = useState("id");
  const [sortDir, setSortDir] = useState("desc");

  // paginate
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const onSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [
    searchClient,
    searchDocNo,
    startDate,
    endDate,
    sortBy,
    sortDir,
    itemsPerPage,
  ]);

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      let start = startDate,
        end = endDate;
      if (start && !end) end = start;
      if (end && !start) start = end;

      const skip = currentPage * itemsPerPage;

      const usp = new URLSearchParams();
      if (searchClient?.trim()) usp.set("customer", searchClient.trim());
      if (searchDocNo?.trim()) usp.set("doc_no", searchDocNo.trim());
      if (start instanceof Date) usp.set("date_from", toYMD(start));
      if (end instanceof Date) usp.set("date_to", toYMD(end));

      const sortCol = sortBy === "contact" ? "customer" : sortBy;
      usp.set("sort_by", sortCol);
      usp.set("sort_dir", sortDir);
      usp.set("skip", String(skip));
      usp.set("limit", String(itemsPerPage));

      const qs = usp.toString();
      // IMPORTANT: endpoint PV
      //const data = await authFetch(`/proces-verbal/${qs ? "?" + qs : ""}`);

      const r = await authFetch(`/proces-verbal/${qs ? "?" + qs : ""}`);
      const data =
        r && typeof r === "object" && typeof r.json === "function"
          ? await r.json()
          : r;

      if (data && Array.isArray(data.items) && typeof data.total === "number") {
        setItems(data.items);
        setTotalCount(data.total);
      } else if (Array.isArray(data)) {
        setItems(data);
        setTotalCount(data.length);
      } else {
        setItems([]);
        setTotalCount(0);
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sigur doriți să ștergeți acest proces verbal?")) {
      return;
    }
    try {
      await authFetch(`/proces-verbal/${id}`, {
        method: "DELETE",
      });
      fetchList();
    } catch (e) {
      alert(`Eroare la ștergere: ${e.message || e}`);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    itemsPerPage,
    searchClient,
    searchDocNo,
    startDate,
    endDate,
    sortBy,
    sortDir,
  ]);

  const pageCount = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const from = totalCount ? currentPage * itemsPerPage + 1 : 0;
  const to = Math.min(totalCount, (currentPage + 1) * itemsPerPage);

  return (
    <Container className="py-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h3 className="mb-0">Lista Procese Verbale</h3>
        </Col>
        <Col xs="auto">
          <Button as={Link} to="/proces_verbal/new" variant="success">
            + Proces verbal nou
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Caută după client"
              value={searchClient}
              onChange={(e) => setSearchClient(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") fetchList();
              }}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Caută după nr. doc"
              value={searchDocNo}
              onChange={(e) => setSearchDocNo(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <InputGroup>
            <InputGroup.Text>De la</InputGroup.Text>
            <DatePicker
              selected={startDate}
              onChange={setStartDate}
              dateFormat="dd.MM.yyyy"
              wrapperClassName="dp-w120"
              className="form-control"
              placeholderText="Start"
              isClearable
              selectsStart
              startDate={startDate}
              endDate={endDate}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <InputGroup>
            <InputGroup.Text>Până la</InputGroup.Text>
            <DatePicker
              selected={endDate}
              onChange={setEndDate}
              dateFormat="dd.MM.yyyy"
              wrapperClassName="dp-w120"
              className="form-control"
              placeholderText="End"
              isClearable
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate || undefined}
            />
          </InputGroup>
        </Col>

        <Col
          md={3}
          className="d-flex justify-content-end align-items-center gap-2"
        >
          <Form.Select
            size="sm"
            style={{ width: 110 }}
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            title="Rânduri pe pagină"
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}/pag.
              </option>
            ))}
          </Form.Select>
          <Button variant="outline-primary" onClick={fetchList}>
            Caută
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setSearchClient("");
              setSearchDocNo("");
              setStartDate(null);
              setEndDate(null);
            }}
          >
            Clear filtre
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">
          {totalCount ? `Afișez ${from}-${to} din ${totalCount}` : "—"}
        </small>
        <small className="text-muted">
          Sort: {sortBy} {sortDir}
        </small>
      </div>

      {loading ? (
        <div className="py-5 text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <SortHeader
                  label="ID"
                  col="id"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Doc No"
                  col="doc_no"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Data Doc"
                  col="doc_date"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Contact"
                  col="contact"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Valoare"
                  col="value"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Linii"
                  col="lines"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <SortHeader
                  label="Finalizat"
                  col="finalized"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
                <th className="text-end">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td>{it.id}</td>
                  <td>{it.bxdoc_no || "-"}</td>
                  <td>{formatDateRo(it.bxdoc_date) || "-"}</td>
                  <td>{it.customer_name ?? it.customer_id ?? "-"}</td>
                  <td>
                    {typeof it.value === "number"
                      ? it.value.toFixed(2)
                      : (it.value ?? "-")}
                  </td>
                  <td>{it.lines ?? 0}</td>
                  <td>{it.finalized ? "✓" : "—"}</td>
                  <td className="text-end">
                    <Button
                      as={Link}
                      to={`/proces-verbal/${it.id}/edit`}
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                    >
                      Editează
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      disabled={it.finalized}
                      onClick={() => handleDelete(it.id)}
                    >
                      Șterge
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    Nu există înregistrări.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <ReactPaginate
            previousLabel={"← Înapoi"}
            nextLabel={"Înainte →"}
            pageCount={pageCount}
            forcePage={currentPage}
            onPageChange={(event) => setCurrentPage(event.selected)}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            containerClassName={"pagination justify-content-center"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            activeClassName={"active"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            renderOnZeroPageCount={null}
          />
        </>
      )}
    </Container>
  );
}
